import { Card, CardContent } from '@/components/ui/card'
import { formatCurrency } from '@/lib/formatters'
import { cn } from '@/lib/utils'
import { type PipelineStats } from '@/models/myPipeline'

interface StatCardProps {
  label: string
  value: string
  subtitle: string
  subtitleClassName?: string
}

function StatCard({ label, value, subtitle, subtitleClassName }: StatCardProps) {
  return (
    <Card>
      <CardContent>
        <div className="text-muted-foreground text-sm">{label}</div>
        <div className="mt-2 text-2xl font-bold tabular-nums">{value}</div>
        <div className={cn('text-muted-foreground mt-1 text-xs', subtitleClassName)}>{subtitle}</div>
      </CardContent>
    </Card>
  )
}

// formatCurrency defaults to 2 decimals; whole dollars need both bounds relaxed.
const WHOLE_DOLLARS = { minimumFractionDigits: 0, maximumFractionDigits: 0 }

function plural(count: number, singular: string): string {
  return `${count} ${singular}${count === 1 ? '' : 's'}`
}

export function PipelineStatCards({ stats }: { stats: PipelineStats }) {
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <StatCard
        label="Total pipeline"
        value={formatCurrency(Number(stats.totalPipeline), WHOLE_DOLLARS)}
        subtitle={
          stats.dealsWithoutValue
            ? `${plural(stats.valuedDeals, 'valued deal')} · ${stats.dealsWithoutValue} without a value`
            : plural(stats.valuedDeals, 'valued deal')
        }
      />
      <StatCard
        label="Expected revenue"
        value={formatCurrency(Number(stats.expectedRevenue), WHOLE_DOLLARS)}
        subtitle={`${formatCurrency(Number(stats.wonValue), WHOLE_DOLLARS)} won · ${stats.valuedOpenDeals} open weighted`}
      />
      <StatCard
        label="Open deals"
        value={String(stats.openDeals)}
        subtitle={
          stats.nextActionsOverdue
            ? `${plural(stats.nextActionsOverdue, 'next action')} overdue`
            : 'No overdue next actions'
        }
        subtitleClassName={stats.nextActionsOverdue ? 'text-red-600' : undefined}
      />
      <StatCard
        label="Win rate"
        value={stats.winRate === null ? '—' : `${Math.round(stats.winRate * 100)}%`}
        subtitle={`${stats.won} won · ${stats.lost} lost · ${stats.openDeals} still open`}
      />
    </div>
  )
}
